'use strict';

import { keys, each, isArray } from 'underscore';


/**
 * Determines whether a given state object is valid, in the sense that it has the right keys defined and those
 * keys have values etc., when compared to another state object.
 *
 * @param s1 The default state object
 * @param s2 A persisted state object.
 * @param objectPath This should be null if called from outside the method. It's the path currently being
 *                      evaluated and is used for recursion.
 * @param isDebug Output debugging statements or not.
 *
 * @returns {boolean} True s2 is valid. Otherwise, false.
 */

const StateValidator = {};

StateValidator.isStateValid = function(s1, s2, objectPath, isDebug) {

    let _printError = function(errorText, path, key) {
        if (isDebug) {
            path ? path = path + "." : path = "";
            console.log(`StateValidator: ${errorText} ${path+key}`);
        }
    }.bind(this);

    let isValid = true;

    let keysList = keys(s1);

    /**
     * Validate each of the s1 keys.
     */
    each(keysList, function(key) {
        // Don't know of another way to break out of this method.
        if (! isValid) { return }

        //  Check for missing key.
        if (! s2.hasOwnProperty(key)) {
            _printError("Missing key:", objectPath, key);
            isValid = false;
            return;
        }

        let s1Value = s1[key];
        let s2Value = s2[key];

        // Make sure the types are the same if the s1 value is defined.
        let t1 = Object.prototype.toString.call(s1Value);
        let t2 = Object.prototype.toString.call(s2Value);
        if (s1Value !== undefined && t1 !== t2) {
            _printError("Type mismatch:", objectPath, key);
            isValid = false;
            return;
        }

        switch (typeof s1Value) {
            case 'string':
                //  Tests for empty.
                let regex = new RegExp("^\\s*$");
                //  See if s1 is empty.
                if (regex.test(s1Value)) {
                    // If not, s2 doesn't need one either.
                } else {
                    // If so, s2 needs one as well.
                    if (regex.test(s2Value)) {
                        _printError("Empty string:" + objectPath, key);
                        isValid = false;
                        return;
                    }
                }
                break;
            case 'object':
                /* Have to see if the object is an array or an object. */
                if (isArray(s1Value)) {
                    if (! isArray(s2Value)) {
                        _printError("Value was not an array: ", objectPath, key);
                        isValid = false;
                        return;
                    }
                    //  If the s1 array has more than one element then the second should have more than one.
                    if (s1Value.length > 0) {
                        if (s2Value.length < 1) {
                            _printError("Empty array:", objectPath, key);
                            isValid = false;
                            return;
                        }
                    }
                } else {
                    /*
                     * Validate objects.
                     */
                    //  Make sure a value is set.
                    if (! s2Value) {
                        _printError("Missing object value:", objectPath, key);
                        isValid = false;
                        return;
                    }

                    //  If the value is an object then call this method recursively.
                    if (t1 === "[object Object]") {
                        let p = key;
                        if (objectPath) {
                            p = objectPath + "." + key;
                        }
                        // Recurse
                        isValid = this.isStateValid(s1Value, s2Value, p, isDebug);
                    }
                }
                break;
            case 'boolean':
            case 'number':
            case 'undefined':
                //  If the first value is undefined then anything in the second is okay.
                break;
            default:
                console.log("StateValidator: Unknown type " + typeof value + " in state object");
        }
    }.bind(this));

    return isValid;
};

export default StateValidator;